import { useState, useEffect } from 'react';
import { Plus, Sparkles, Send, Trash2, Mail, Loader2, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OutreachTab({ token }) {
    const [prospects, setProspects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [emailDraft, setEmailDraft] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');

    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

    const load = async () => {
        setLoading(true);
        try {
            const r = await fetch(`${API}/api/outreach/prospects`, { headers });
            const d = await r.json();
            setProspects(d.prospects || []);
        } catch {
            setError('Kon prospects niet laden');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const addProspect = async (form) => {
        try {
            const r = await fetch(`${API}/api/outreach/prospects`, {
                method: 'POST',
                headers,
                body: JSON.stringify(form),
            });
            if (!r.ok) throw new Error();
            setShowAdd(false);
            load();
        } catch {
            setError('Toevoegen mislukt');
        }
    };

    const removeProspect = async (id) => {
        if (!confirm('Verwijderen?')) return;
        await fetch(`${API}/api/outreach/prospects/${id}`, { method: 'DELETE', headers });
        load();
    };

    const generateEmail = async (prospect) => {
        setGenerating(true);
        setError('');
        try {
            const r = await fetch(`${API}/api/outreach/generate-email`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ prospect_id: prospect.id, taal: 'nl' }),
            });
            if (!r.ok) throw new Error(await r.text());
            const data = await r.json();
            setEmailDraft({ ...data, prospect_id: prospect.id, prospect_naam: prospect.naam, prospect_email: prospect.email });
        } catch (e) {
            setError('AI-generatie mislukt: ' + e.message);
        } finally {
            setGenerating(false);
        }
    };

    const sendEmail = async () => {
        if (!emailDraft) return;
        setSending(true);
        try {
            const r = await fetch(`${API}/api/outreach/send-email`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    prospect_id: emailDraft.prospect_id,
                    onderwerp: emailDraft.onderwerp,
                    inhoud_html: emailDraft.inhoud_html,
                    inhoud_text: emailDraft.inhoud_text,
                }),
            });
            if (!r.ok) throw new Error();
            setEmailDraft(null);
            load();
        } catch {
            setError('Versturen mislukt');
        } finally {
            setSending(false);
        }
    };

    if (loading) return <div className="text-gray-500">Laden...</div>;

    return (
        <div className="space-y-4" data-testid="outreach-tab">
            <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold font-['Outfit']">Prospects ({prospects.length})</h2>
                <button
                    onClick={() => setShowAdd(true)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 rounded-lg text-sm font-medium"
                    data-testid="add-prospect-btn"
                >
                    <Plus className="w-4 h-4" /> Prospect toevoegen
                </button>
            </div>

            {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg px-4 py-2 text-sm">{error}</div>}

            {prospects.length === 0 ? (
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center text-gray-500">
                    Nog geen prospects. Voeg er een toe om AI-mails te genereren.
                </div>
            ) : (
                <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="text-xs text-gray-600 uppercase border-b border-gray-800">
                            <tr>
                                <th className="px-4 py-3 text-left">Naam</th>
                                <th className="px-4 py-3 text-left">Email</th>
                                <th className="px-4 py-3 text-left">Locatie</th>
                                <th className="px-4 py-3 text-center">Douches</th>
                                <th className="px-4 py-3 text-center">Status</th>
                                <th className="px-4 py-3 text-center">Mails</th>
                                <th className="px-4 py-3 text-right">Acties</th>
                            </tr>
                        </thead>
                        <tbody>
                            {prospects.map((p) => (
                                <tr key={p.id} className="border-b border-gray-800/50 hover:bg-gray-800/20" data-testid={`prospect-row-${p.id}`}>
                                    <td className="px-4 py-3 text-white font-medium">{p.naam}</td>
                                    <td className="px-4 py-3 text-gray-400">{p.email}</td>
                                    <td className="px-4 py-3 text-gray-400">{p.locatie || '-'}</td>
                                    <td className="px-4 py-3 text-center text-gray-300">{p.geschat_douches}</td>
                                    <td className="px-4 py-3 text-center">
                                        <span className={`px-2 py-0.5 rounded-full text-xs ${p.status === 'verstuurd' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-gray-800 text-gray-400'}`}>
                                            {p.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-center text-gray-300">{p.emails_sent}</td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex items-center gap-2 justify-end">
                                            <button
                                                onClick={() => generateEmail(p)}
                                                disabled={generating}
                                                className="flex items-center gap-1 px-2 py-1 bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 rounded text-xs disabled:opacity-50"
                                                data-testid={`generate-email-${p.id}`}
                                            >
                                                {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                                AI mail
                                            </button>
                                            <button
                                                onClick={() => removeProspect(p.id)}
                                                className="p-1 text-gray-500 hover:text-red-400"
                                                data-testid={`delete-prospect-${p.id}`}
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showAdd && <AddProspectModal onClose={() => setShowAdd(false)} onSave={addProspect} />}

            {emailDraft && (
                <EmailPreviewModal
                    draft={emailDraft}
                    setDraft={setEmailDraft}
                    onSend={sendEmail}
                    onClose={() => setEmailDraft(null)}
                    sending={sending}
                />
            )}
        </div>
    );
}

function AddProspectModal({ onClose, onSave }) {
    const [form, setForm] = useState({ naam: '', email: '', telefoon: '', website: '', locatie: '', geschat_douches: 10, notities: '' });
    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" data-testid="add-prospect-modal">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-lg">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold">Nieuwe Prospect</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <div className="space-y-3">
                    {[
                        ['naam', 'Bedrijfsnaam *'],
                        ['email', 'E-mail *'],
                        ['telefoon', 'Telefoon'],
                        ['website', 'Website'],
                        ['locatie', 'Locatie / plaats'],
                    ].map(([key, label]) => (
                        <input
                            key={key}
                            type={key === 'email' ? 'email' : 'text'}
                            placeholder={label}
                            value={form[key]}
                            onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500/50"
                            data-testid={`prospect-input-${key}`}
                        />
                    ))}
                    <input
                        type="number"
                        placeholder="Geschat aantal douches"
                        value={form.geschat_douches}
                        onChange={(e) => setForm({ ...form, geschat_douches: parseInt(e.target.value) || 10 })}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500/50"
                        data-testid="prospect-input-douches"
                    />
                    <textarea
                        placeholder="Notities (optioneel)"
                        value={form.notities}
                        onChange={(e) => setForm({ ...form, notities: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500/50"
                        data-testid="prospect-input-notities"
                    />
                </div>
                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onClose} className="px-4 py-2 text-gray-400 hover:text-white text-sm">Annuleren</button>
                    <button
                        onClick={() => onSave(form)}
                        disabled={!form.naam || !form.email}
                        className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg text-sm font-medium disabled:opacity-50"
                        data-testid="save-prospect-btn"
                    >
                        Toevoegen
                    </button>
                </div>
            </div>
        </div>
    );
}

function EmailPreviewModal({ draft, setDraft, onSend, onClose, sending }) {
    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" data-testid="email-preview-modal">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-lg font-bold flex items-center gap-2"><Mail className="w-5 h-5 text-purple-400" /> AI-gegenereerde mail</h3>
                        <p className="text-sm text-gray-500">Voor: {draft.prospect_naam} ({draft.prospect_email})</p>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Onderwerp</label>
                        <input
                            value={draft.onderwerp}
                            onChange={(e) => setDraft({ ...draft, onderwerp: e.target.value })}
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                            data-testid="email-onderwerp-input"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Inhoud (bewerkbaar)</label>
                        <textarea
                            value={draft.inhoud_text}
                            onChange={(e) => setDraft({ ...draft, inhoud_text: e.target.value, inhoud_html: e.target.value.split('\n').map(p => `<p>${p}</p>`).join('') })}
                            rows={12}
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm font-mono"
                            data-testid="email-inhoud-input"
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onClose} className="px-4 py-2 text-gray-400 hover:text-white text-sm">Annuleren</button>
                    <button
                        onClick={onSend}
                        disabled={sending}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg text-sm font-medium disabled:opacity-50"
                        data-testid="send-email-btn"
                    >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Verstuur mail
                    </button>
                </div>
            </div>
        </div>
    );
}
